import { SharedComponentsProps } from '../../../';
import { ReactNode } from 'react';

export type SlideChangeSources =
  | 'autoplay'
  | 'arrow'
  | 'dot'
  | 'swipe'
  | 'prop'
  | null;
export type slideDirection = 'prev' | 'next';

export const AvailableSlideTransition = ['fade', 'slide', 'rotate'] as const;
export type AvailableSlideTransitionType =
  typeof AvailableSlideTransition[number];

export type SlideProps = {
  id: string | number;
};

export type renderComponentsParams = {
  item: SlideProps;
  isSelected?: boolean;
};

export type renderComponentsMethod = (
  params: renderComponentsParams,
) => ReactNode;

export type controlsWrapperParams = {
  children: ReactNode;
};

export type SliderSlideListWrapperParams = {
  children: ReactNode;
};

export type SliderProps = SharedComponentsProps & {
  /**
   * Array for slides data
   */
  data?: SlideProps[];
  /**
   * Method to display the slide from a data
   */
  renderComponentMethod?: (params: renderComponentsParams) => ReactNode;
  /**
   * Custom current index set by props
   */
  customCurrentIndex?: number;
  /**
   * Define if the Slider is infinite
   */
  isInfinite?: boolean;
  /**
   * Item by lines displayed
   */
  itemByLine?: number;
  /**
   * number of line displayed
   */
  lineBySlide?: number;
  /**
   * Rotation transition number of items visible
   */
  numberOfItemsVisibleForRotateTransition?: number;
  /**
   * Rotation transition translation percentage
   */
  translationPercentageForRotateTransition?: number;
  /**
   * Slide transition spacing between slide
   */
  spacingForSlide?: string;
  /**
   * Slide transition when slide is entering or leaving the screen
   */
  transitionType?: AvailableSlideTransitionType;
  /**
   * Slide transition time (in ms)
   */
  transitionTime?: number;
  /**
   * Slide easing for transition
   */
  transitionEasing?: string;
  /**
   * Slider should autoplay
   */
  isAutoplayEnabled?: boolean;
  /**
   * Delay between two autoplay
   */
  autoplayDelay?: number;
  /**
   * Should pause autoplay on Slider hover
   */
  autoplayPauseOnHover?: boolean;
  /**
   * Slider visibility for user to pause / run autoplay
   */
  percentageVisibleForAutoplay?: number;
  /**
   * Function to render slideList's wrapper
   */
  slideListWrapper?: (params: SliderSlideListWrapperParams) => ReactNode;
  /**
   * Left padding for slide list
   */
  slideListPaddingLeft?: string;
  /**
   * Right padding for slide list
   */
  slideListPaddingRight?: string;
  /**
   * Function to render control's wrapper
   */
  controlsWrapper?: (params: controlsWrapperParams) => ReactNode;
  /**
   * Allow swipe for Slider
   */
  isSwipeEnabled?: boolean;
  /**
   * delta used to determined if it's a swipe
   */
  deltaForSwiping?: number;
  /**
   * display arrows controls for the Slider
   */
  isArrowsEnabled?: boolean;
  /**
   * Override arrows component class
   */
  arrowsComponentClassname?: string;
  /**
   * Add arrows custom class
   */
  arrowsComponentCustomClassname?: string;
  /**
   * Override arrow component class
   */
  arrowComponentClassname?: string;
  /**
   * Add arrow custom class
   */
  arrowComponentCustomClassnames?: string;
  /**
   * Prev arrow children node
   */
  prevArrowChildren?: ReactNode;
  /**
   * Next arrow children node
   */
  nextArrowChildren?: ReactNode;
  /**
   * display dots controls for the Slider
   */
  isDotsEnabled?: boolean;
  /**
   * Override dots component class
   */
  dotsComponentClassname?: string;
  /**
   * Add dots custom class
   */
  dotsComponentCustomClassnames?: string;
  /**
   * Override dot component class
   */
  dotComponentClassname?: string;
  /**
   * Add dot custom class
   */
  dotComponentCustomClassname?: string;
  /**
   * Function to render dot
   */
  renderDotFunction?: (index: number) => ReactNode;
  /**
   * dot text for screen reader
   */
  dotSrOnlyTextFunction?: (index: number) => ReactNode;
  /**
   * slider description text for screen reader
   */
  descriptionSrOnlyText?: string;
  /**
   * Callback when slide change
   */
  onSlideChange?: (slideId: number, source: SlideChangeSources) => void;
  /**
   * Callback after slide change
   */
  onAfterSlideChange?: (slideId: number, source: SlideChangeSources) => void;
  /**
   * Callback when slide change from Autoplay
   */
  onAutoplaySlideChange?: (slideId: number) => void;
  /**
   * Callback after slide change from Autoplay
   */
  onAfterAutoplaySlideChange?: (slideId: number) => void;
  /**
   * Callback when slide change from arrows
   */
  onArrowClick?: (slideId: number, direction: slideDirection) => void;
  /**
   * Callback after slide change from arrows
   */
  onAfterArrowClick?: (slideId: number, direction: slideDirection) => void;
  /**
   * Callback when slide change from dots
   */
  onDotClick?: (slideId: number) => void;
  /**
   * Callback after slide change from dots
   */
  onAfterDotClick?: (slideId: number) => void;
  /**
   * Callback when slide change from swiper
   */
  onSwipe?: (slideId: number) => void;
  /**
   * Callback after slide change from swipe
   */
  onAfterSwipe?: (slideId: number) => void;
  /**
   * Show slider or not if there isn't enougth items
   */
  disableSliderIfNotEnoughItems?: boolean;
};

export type StyledSliderProps = SliderProps & {
  currentSlideIndex?: number;
  customStyle?: SliderProps['customStyle'];
};

export interface SliderReducerState {
  currentSlideIndex: number;
  maxIndex: number;
  latestSource: SlideChangeSources;
  direction: string | null;
}

export interface SliderReducerAction {
  type: string;
  payload?: any;
}
